/**
 * Outline SSE consumer (Phase 1 / Step 8).
 *
 * Thin async-iterable wrappers around the four agentic SSE endpoints exposed
 * by `OutlineController` (Step 5). Pattern copied from
 * `services/assistant/api.ts` — fetch + ReadableStream because the browser's
 * `EventSource` can't carry a Bearer token or POST body.
 *
 * Endpoints (gateway-relative; controller mounts at `/api/v1/outlines`):
 *   POST /api/core/outlines/generate
 *   POST /api/core/outlines/{id}/ai-edit
 *   POST /api/core/outlines/{id}/topics/{tid}/preview-content
 *   POST /api/core/outlines/{id}/topics/{tid}/refine-content
 *
 * Wire shapes — see plan §0.1 "Step 5 summary":
 *   event: progress  data: { message }
 *   event: partial   data: { outline | content }
 *   event: complete  data: { outline | content, agent, latency_ms, retries }
 *   event: persisted data: { kind, outline }   ← Java-only, OutlineResponse shape
 *   event: error     data: { message, code }
 *
 * Important: `outline`/`content` payloads on partial/complete come from the
 * Python ai-service in **snake_case** (CourseOutlineSchema / GeneratedContentSchema).
 * The `persisted` event's `outline` is the Java `OutlineResponse` in
 * **camelCase**. Callers that need to merge both into the editor store must
 * normalise — see `applyStreamingPartial` / `applyPersistedIds` in
 * `stores/outline-editor-store.ts`.
 *
 * Cancellation: every generator accepts an `AbortSignal`. Aborting causes the
 * underlying fetch to reject with `DOMException("AbortError")`; the generator
 * silently completes (no event emitted, no rejection bubbled to the caller).
 *
 * @author Tutara Team
 * @since 2026-04 (Phase 1 / Step 8 - agentic course authoring)
 */

import { API_BASE_URL } from '@/config/env';
import { authenticatedFetch } from '@/services/api/interceptor';
import type { OutlineResponse } from './types';

// =============================================================================
// AI-shape (snake_case) types — mirror ai-service/core/agentic/schemas.py
// =============================================================================

export interface AiTopicSchema {
  topic_id?: string;
  title: string;
  summary?: string;
  learning_objectives?: string[];
  depth_hint?: 'OVERVIEW' | 'STANDARD' | 'DEEP';
  personalization?: 'ADAPT' | 'VERBATIM';
  order_index?: number;
  estimated_minutes?: number;
}

export interface AiModuleSchema {
  module_id?: string;
  title: string;
  description?: string;
  order_index?: number;
  topics?: AiTopicSchema[];
}

export interface AiCourseOutlineSchema {
  outline_id?: string;
  title: string;
  description?: string;
  audience?: string;
  target_role?: string;
  language?: string;
  difficulty?: string;
  modules?: AiModuleSchema[];
  version?: number;
  status?: string;
}

export interface AiGeneratedContentSchema {
  topic_id?: string;
  body_mdx?: string;
  key_takeaways?: string[];
  estimated_read_minutes?: number;
  citations?: string[];
  /** Phase 4e — short label distinguishing parallel alternatives. */
  approach_label?: string;
}

// =============================================================================
// SSE event union
// =============================================================================

export interface OutlineSseProgress {
  type: 'progress';
  message: string;
}

export interface OutlineSsePartial {
  type: 'partial';
  /** Present on outline-shaped streams (`generate`, `ai-edit`). */
  outline?: AiCourseOutlineSchema;
  /** Present on content-shaped streams (`preview-content`, `refine-content`). */
  content?: AiGeneratedContentSchema;
}

export interface OutlineSseComplete {
  type: 'complete';
  outline?: AiCourseOutlineSchema;
  content?: AiGeneratedContentSchema;
  /** Phase 4e — populated by generate-alternatives. Three entries on success. */
  alternatives?: AiGeneratedContentSchema[];
  agent?: string;
  latencyMs?: number;
  retries?: number;
}

export type PersistedKind = 'outline_generated' | 'outline_edited' | 'content_refined';

export interface OutlineSsePersisted {
  type: 'persisted';
  kind: PersistedKind;
  /** Server-shape outline (camelCase) with assigned ids. */
  outline: OutlineResponse;
}

export interface OutlineSseError {
  type: 'error';
  message: string;
  code?: 'agent_error' | 'internal_error';
}

export type OutlineSseEvent =
  | OutlineSseProgress
  | OutlineSsePartial
  | OutlineSseComplete
  | OutlineSsePersisted
  | OutlineSseError;

// =============================================================================
// Request bodies (camelCase — Jackson on Java side)
// =============================================================================

export interface StreamGenerateRequest {
  topic: string;
  audience?: string;
  difficulty?: string;
  language?: string;
  targetRole?: string;
  roughStructure?: string;
}

export interface StreamAiEditRequest {
  prompt: string;
}

export interface StreamPreviewContentRequest {
  refinementPrompt?: string;
}

export interface StreamRefineContentRequest {
  prompt: string;
}

// =============================================================================
// Configuration
// =============================================================================

const OUTLINES_BASE = `${API_BASE_URL}/api/core/outlines`;

// =============================================================================
// SSE parsing — same approach as services/assistant/api.ts
// =============================================================================

/**
 * Parse a chunk of complete SSE event blocks (each terminated by `\n\n`)
 * into discrete `{ event, data }` records. Tolerates both spaced and
 * unspaced field forms (Python emits `event: x`, Spring emits `event:x`).
 */
function parseSseBlocks(raw: string): Array<{ event: string; data: string }> {
  const events: Array<{ event: string; data: string }> = [];
  for (const block of raw.split('\n\n')) {
    const trimmed = block.trim();
    if (!trimmed) continue;
    let event = '';
    // Per the SSE spec, a multi-line `data:` value is split across multiple
    // `data:` lines and reassembled on the client by joining with '\n'.
    // Spring serializes our `persisted` payload via the primary ObjectMapper
    // which has INDENT_OUTPUT enabled, so the JSON arrives as multiple
    // `data:` lines — overwriting on each line drops the persisted event.
    const dataLines: string[] = [];
    for (const line of trimmed.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trimStart();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    }
    const data = dataLines.join('\n');
    if (event && data) events.push({ event, data });
  }
  return events;
}

/** Map a raw `{event, data}` SSE record to a typed `OutlineSseEvent` (or null). */
function toTypedEvent(event: string, data: string): OutlineSseEvent | null {
  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(data) as Record<string, unknown>;
  } catch (err) {
    console.warn('[outlineSse] Failed to parse SSE data as JSON', { event, err });
    return null;
  }
  switch (event) {
    case 'progress':
      return {
        type: 'progress',
        message: typeof payload.message === 'string' ? payload.message : '',
      };
    case 'partial':
      return {
        type: 'partial',
        outline: payload.outline as AiCourseOutlineSchema | undefined,
        content: payload.content as AiGeneratedContentSchema | undefined,
      };
    case 'complete':
      return {
        type: 'complete',
        outline: payload.outline as AiCourseOutlineSchema | undefined,
        content: payload.content as AiGeneratedContentSchema | undefined,
        alternatives: Array.isArray(payload.alternatives)
          ? (payload.alternatives as AiGeneratedContentSchema[])
          : undefined,
        agent: payload.agent as string | undefined,
        // Java/Python camelCase mismatch — accept both.
        latencyMs:
          typeof payload.latency_ms === 'number'
            ? payload.latency_ms
            : typeof payload.latencyMs === 'number'
              ? payload.latencyMs
              : undefined,
        retries: typeof payload.retries === 'number' ? payload.retries : undefined,
      };
    case 'persisted': {
      const kind = payload.kind as PersistedKind | undefined;
      const outline = payload.outline as OutlineResponse | undefined;
      if (!kind || !outline) {
        console.warn('[outlineSse] persisted event missing kind/outline', payload);
        return null;
      }
      return { type: 'persisted', kind, outline };
    }
    case 'error':
      return {
        type: 'error',
        message: typeof payload.message === 'string' ? payload.message : 'Stream error',
        code: payload.code as OutlineSseError['code'],
      };
    default:
      // Unknown event types: silently ignore so future server additions don't
      // break older clients.
      return null;
  }
}

// =============================================================================
// Core stream driver
// =============================================================================

/**
 * Drive a single POST-SSE call, yielding typed events as they arrive.
 *
 * Aborts triggered via `signal` resolve the generator without throwing — the
 * caller is the one that initiated the abort, so a thrown AbortError is
 * just noise.
 *
 * Network / non-2xx responses surface a synthetic `error` event so callers
 * have one uniform handling path.
 */
async function* streamSse(
  url: string,
  body: object | undefined,
  signal: AbortSignal | undefined,
): AsyncGenerator<OutlineSseEvent, void, void> {
  let response: Response;
  try {
    response = await authenticatedFetch(url, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      signal,
    });
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') return;
    const message = err instanceof Error ? err.message : 'Network error';
    yield { type: 'error', message, code: 'internal_error' };
    return;
  }

  if (!response.ok) {
    let message = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const text = await response.text();
      if (text) {
        try {
          const parsed = JSON.parse(text) as Record<string, unknown>;
          const m = parsed.error ?? parsed.message ?? parsed.detail;
          if (typeof m === 'string' && m.length > 0) message = m;
        } catch {
          // not JSON — keep default message
        }
      }
    } catch {
      // ignore body-read failures
    }
    yield { type: 'error', message, code: 'internal_error' };
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    yield { type: 'error', message: 'Response body is empty', code: 'internal_error' };
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Drain complete event blocks (\n\n-terminated). Keep the trailing
      // partial block in the buffer for the next iteration.
      const lastBoundary = buffer.lastIndexOf('\n\n');
      if (lastBoundary === -1) continue;
      const ready = buffer.slice(0, lastBoundary + 2);
      buffer = buffer.slice(lastBoundary + 2);

      for (const { event, data } of parseSseBlocks(ready)) {
        const typed = toTypedEvent(event, data);
        if (typed) yield typed;
      }
    }

    // Flush any remaining buffer (server didn't terminate with \n\n).
    if (buffer.trim()) {
      for (const { event, data } of parseSseBlocks(buffer)) {
        const typed = toTypedEvent(event, data);
        if (typed) yield typed;
      }
    }
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') return;
    const message = err instanceof Error ? err.message : 'Stream read error';
    yield { type: 'error', message, code: 'internal_error' };
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // already released
    }
  }
}

// =============================================================================
// Public generators
// =============================================================================

/**
 * Stream outline generation from a topic prompt.
 * `POST /api/core/outlines/generate`
 */
export function streamGenerate(
  request: StreamGenerateRequest,
  signal?: AbortSignal,
): AsyncGenerator<OutlineSseEvent, void, void> {
  return streamSse(`${OUTLINES_BASE}/generate`, request, signal);
}

/**
 * Stream a natural-language edit applied to an existing outline.
 * `POST /api/core/outlines/{outlineId}/ai-edit`
 */
export function streamAiEdit(
  outlineId: string,
  request: StreamAiEditRequest,
  signal?: AbortSignal,
): AsyncGenerator<OutlineSseEvent, void, void> {
  return streamSse(`${OUTLINES_BASE}/${outlineId}/ai-edit`, request, signal);
}

/**
 * Stream a content preview for a single topic. No persistence — caller is
 * expected to follow up with `streamRefineContent` (or accept-as-is) to
 * actually save the draft.
 * `POST /api/core/outlines/{outlineId}/topics/{topicId}/preview-content`
 */
export function streamPreviewContent(
  outlineId: string,
  topicId: string,
  request?: StreamPreviewContentRequest,
  signal?: AbortSignal,
): AsyncGenerator<OutlineSseEvent, void, void> {
  return streamSse(
    `${OUTLINES_BASE}/${outlineId}/topics/${topicId}/preview-content`,
    request ?? {},
    signal,
  );
}

/**
 * Stream a refinement of an existing topic's content. The server persists
 * the accepted result on `complete` and emits a final `persisted` event.
 * `POST /api/core/outlines/{outlineId}/topics/{topicId}/refine-content`
 */
export function streamRefineContent(
  outlineId: string,
  topicId: string,
  request: StreamRefineContentRequest,
  signal?: AbortSignal,
): AsyncGenerator<OutlineSseEvent, void, void> {
  return streamSse(
    `${OUTLINES_BASE}/${outlineId}/topics/${topicId}/refine-content`,
    request,
    signal,
  );
}

/**
 * Phase 4e — stream three side-by-side alternative versions of a topic body.
 * Ephemeral; tutor accepts one via the existing refine flow. The server emits
 * a single `complete` event whose payload carries `alternatives: [...]`.
 * `POST /api/core/outlines/{outlineId}/topics/{topicId}/generate-alternatives`
 */
export function streamGenerateAlternatives(
  outlineId: string,
  topicId: string,
  signal?: AbortSignal,
): AsyncGenerator<OutlineSseEvent, void, void> {
  return streamSse(
    `${OUTLINES_BASE}/${outlineId}/topics/${topicId}/generate-alternatives`,
    {},
    signal,
  );
}

export default {
  streamGenerate,
  streamAiEdit,
  streamPreviewContent,
  streamRefineContent,
  streamGenerateAlternatives,
};
